import styled, { css, useTheme } from 'styled-components';
import { AngleBoldRight16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import { Badge, ellipsisMixin } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import ToggleIcon from '../../icons/toggle.svg?react';
import { useA11yState } from '../hooks/use-a11y-state';
import { useNavigation } from '../hooks/use-navigation';

export function MoreSettings() {
  const { t } = useLingui();
  const { modifiedSettingsCount } = useA11yState();
  const theme = useTheme();
  const { go } = useNavigation();

  const handleClick = () => go('settings');

  const showBadge = modifiedSettingsCount > 0;

  return (
    <Container
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <Icon
        component={ToggleIcon}
        size={24}
        fill={theme.moreSettings.iconColor}
      />
      <Wrapper>
        <Label>{t`More Settings`}</Label>
        {showBadge && (
          <Badge
            size="large"
            borderMode="circular"
            color={theme.moreSettings.badgeColor}
            style={{ flexShrink: 0 }}
          >
            {modifiedSettingsCount}
          </Badge>
        )}
      </Wrapper>
      <Icon
        component={AngleBoldRight16}
        size={16}
        fill={theme.moreSettings.arrowColor}
        style={{ flexShrink: 0 }}
      />
    </Container>
  );
}

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 15px;
    border-radius: 100px;
    cursor: pointer;
    background-color: ${theme.moreSettings.backgroundColor};
    transition: background-color 0.15s;

    &:hover {
      background-color: ${theme.moreSettings.hoverBackgroundColor};
    }

    &:focus-visible {
      background-color: ${theme.moreSettings.hoverBackgroundColor};
      outline: 3px solid ${theme.moreSettings.outlineColor};
      outline-offset: 2px;
      z-index: 1;
    }

    @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
      background-color: ${theme.moreSettings.mobile.backgroundColor};
      padding: 15px;
      box-shadow:
        0px 14px 4px 0px rgba(0, 0, 0, 0),
        0px 9px 4px 0px rgba(0, 0, 0, 0),
        0px 5px 3px 0px rgba(0, 0, 0, 0.02),
        0px 2px 2px 0px rgba(0, 0, 0, 0.03),
        0px 1px 1px 0px rgba(0, 0, 0, 0.03);

      &:hover {
        background-color: ${theme.moreSettings.mobile.hoverBackgroundColor};
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

const Wrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.div`
  ${({ theme }) => css`
    color: ${theme.moreSettings.labelColor};
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    flex-shrink: 1;

    ${ellipsisMixin}
  `}
`;
